import { gql } from "@apollo/client"

export type ProductPropType = {
    ID: number;
    DisplayName: string;
    DropdownName: string;
    Name: string;
    ProductSelectorName: string;
    ProductTypeID: number;
    Description: string;
    Features: string;
    Url: string;
    ProductCode: string;
    ProductImageIDs: number[];
    HidePriceFromUnapprovedUsers: boolean;
    SealIDs: number[];
    POA: boolean;
    RelatedProductGroupID: number;
    ProductRelatedProductIDs: number[];
    ProductMinimumOrders: {
        Quantity: number;
        QuantityMultiple: number;
        PricingRegionID: number;
    }[]
    DefaultImage: {
        Value?: {
            ID: number;
            ProductID: number;
            ImageUrl: string;
        }
    }
    ProductImages: {
        ID: number;
        ProductID: number;
        ImageUrl: string;
    }[]
    ProductType: {
        Value?: {
            ID: number;
            Description: string;
        }
    }
    ProductAttachments: {
        Name: string;
        FileUrl: string;
        ProductAttachmentType: {
            Value: {
                ID: number;
                Description: string;
                DisplayOnWebsite: boolean;
            }
        }
    }[]
    Cores: {
        MaterialType: {
            Value?: {
                ID: number;
                Description: string;
            }
        }
    }[];
    Seals: {
        ID: number;
        Name: string;
        SealProductID: number;
        ProductID: number;
        IsCombinationSeal: boolean;
        SealProduct: {
            Value?: {
                ID: number;
                Name: string
                SealTypeIDs: number[];
                SealTypes: {
                    ID: number;
                    Description: string;
                }[]
            }
        }
    }[]
    ProductIcons: {
        ID: number;
        IconUrl: string;
        LinkUrl: string;
        Tooltip: string;
        ListOrder: number;
    }[]
}

export const ProductScreenQuery = gql`
query(
    $id: Int!
) {
    screen: Product(
        id: $id
    ) {
        ID
        DisplayName
        DropdownName
        Name
        ProductSelectorName
        ProductTypeID
        Description
        Features
        Url
        ProductImageIDs
        ProductCode
        POA
        SealIDs
        HidePriceFromUnapprovedUsers
        RelatedProductGroupID
        ProductRelatedProductIDs
        ProductMinimumOrders {
            Quantity
            QuantityMultiple
            PricingRegionID
        }
        DefaultImage {
            Value {
                ID
                ProductID
                ImageUrl
            }
        }
        ProductImages {
            ID
            ProductID
            ImageUrl
        }
        ProductType {
            Value {
                ID
                Description
            }
        }
        ProductAttachments {
            Name
            FileUrl
            ProductAttachmentType {
                Value {
                    ID
                    Description
                    DisplayOnWebsite
                }
            }
        }
        Cores {
            MaterialType {
                Value {
                    ID
                    Description
                }
            }
        }
        Seals {
            ID
            Name
            SealProductID
            ProductID
            IsCombinationSeal
            SealProduct {
                Value {
                    ID
                    Name
                    SealTypeIDs
                    SealTypes {
                        ID
                        Description
                    }
                }
            }
        }
        ProductIcons {
            ID
            IconUrl
            LinkUrl
            Tooltip
            ListOrder
        }
    }
}
`

export type ProductLinePropType = {
    ID: number;
    Name: string;
    Description: string;
    Features: string;
    RelatedProductGroupID: number;
    RelatedProducts: {
        ID: number;
        Name: string;
        DisplayName: string;
        ProductImages: {
            ID: number;
            ImageUrl: string;
        }[]
    }[]
    ProductLineAttachments: {
        Name: string;
        FileUrl: string;
        ProductAttachmentType: {
            Value: {
                ID: number;
                Description: string;
                DisplayOnWebsite: boolean;
            }
        }
    }[]
    ProductLineImages: {
        ImageUrl: string;
    }[]
    ProductLineIcons: {
        IconFileKey: string;
    }[]
    MaterialTypeID: number;
    MaterialType: {
        Value?: {
            Description: string;
        }
    }
}

export const ProductLineScreenQuery = gql`
query(
    $id: Int!
) {
    screen: ProductLine(
        id: $id
    ) {
        ID
        Name
        Description
        Features
        RelatedProductGroupID
        RelatedProducts {
            ID
            Name
            DisplayName
            ProductImages {
                ID
                ImageUrl
            }
        }
        ProductLineAttachments {
            Name
            FileUrl
            ProductAttachmentType {
                Value {
                    ID
                    Description
                    DisplayOnWebsite
                }
            }
        }
        ProductLineImages {
            ImageUrl
        }
        ProductLineIcons {
          IconFileKey
        }
        MaterialTypeID
        MaterialType {
            Value {
                Description
            }
        }
    }
}
`

export const ProductLinesSearchQuery = gql`
query ($where: ProductLineFilterInput){
    search: ProductLines (
        where: $where
    ) {
        items {
            ID
            Name
            ProductLineImages {
                ImageUrl
            }
        }
    }
}
`

export const SealProductsSearchQuery = gql`
query ($where: SealProductFilterInput){
    search: SealProducts (
        where: $where
    ) {
        items {
            ID
            Name
            ShopSealTypeIDs
            SealProductImages {
                ImageUrl
            }
            DefaultSealProductImage {
                Value {
                    ID
                    ImageUrl
                }
            }
        }
    }
}
`

export const ProductsByProductLineDropdownQuery = gql`
query ($where: ProductFilterInput){
    dropdown: Products (
        where: $where,
        order: { DropdownName: ASC }
    ) {
        items {
            value: ID
            label: DropdownName
            DisplayName
            ProductCode
            POA
            ProductMinimumOrders {
                Quantity
                QuantityMultiple
                PricingRegionID
            }
            HidePriceFromUnapprovedUsers
            ProductIcons {
                ID
                IconUrl
                LinkUrl
                Tooltip
                ListOrder
            }
        }
    }
}
`

export const ProductsBySealProductDropdownQuery = gql`
query ($where: ProductFilterInput){
    dropdown: Products (
        where: $where,
        order: { DropdownName: ASC }
    ) {
        items {
            value: ID
            label: DropdownName
            DisplayName
            ProductCode
            POA
            ProductMinimumOrders {
                Quantity
                QuantityMultiple
                PricingRegionID
            }
            HidePriceFromUnapprovedUsers
            ProductImages {
                ImageUrl
            }
            Features
            Description
            ProductAttachments {
                Name
                FileUrl
                ProductAttachmentType {
                    Value {
                        ID
                        Description
                        DisplayOnWebsite
                    }
                }
            }
            ProductIcons {
                ID
                IconUrl
                LinkUrl
                Tooltip
                ListOrder
            }
        }
    }
}
`

export const ProductsByMasterProductDropdownQuery = gql`
query ($where: ProductFilterInput){
    dropdown: Products (
        where: $where,
        order: { DropdownName: ASC }
    ) {
        items {
            value: ID
            label: DropdownName
            DisplayName
            ProductCode
            POA
            Description
            Features
            ProductAttachments {
                Name
                FileUrl
                ProductAttachmentType {
                    Value {
                        ID
                        Description
                        DisplayOnWebsite
                    }
                }
            }
            RelatedProductGroupID
            ProductRelatedProductIDs
            ProductMinimumOrders {
                Quantity
                QuantityMultiple
                PricingRegionID
            }
            HidePriceFromUnapprovedUsers
            ProductImages {
                ImageUrl
            }
            ProductIcons {
                ID
                IconUrl
                LinkUrl
                Tooltip
                ListOrder
            }
        }
    }
}
`


export type SealProductPropType = {
    ID: number;
    Name: string;
    Code: string;
    Description: string;
    Features: string;
    RelatedProductGroupID: number;
    SealTypeIDs : number[];
    SealTypes : {
        ID : number;
    }
    ShopSealTypeIDs: number[],
    ShopSealTypes: {
        value : {
            ID : number;
            Description :string
        }
    }
    RelatedProducts: {
        ID: number;
        Name: string;
        DisplayName: string;
        ProductImages: {
            ID: number;
            ImageUrl: string;
        }[]
    }[]
    SealProductAttachments: {
        Name: string;
        FileUrl: string;
        ProductAttachmentType: {
            Value: {
                ID: number;
                Description: string;
                DisplayOnWebsite: boolean;
            }
        }
    }[]
    SealProductImages: {
        ImageUrl: string;
    }[]
    DefaultSealProductImage: {
        Value: {
            ID: number;
            ImageUrl: string;
        }
    }
    ProductIcons: {
        ID: number;
        IconUrl: string;
        LinkUrl: string;
        Tooltip: string;
        ListOrder: number;
    }[]
}

export type MasterProductPropType = {
    ID: number;
    Name: string;
    Description: string;
    Features: string;
    Code: string;
    RelatedProductGroupID: number;
    RelatedProducts: {
        ID: number;
        Name: string;
        DisplayName: string;
        ProductImages: {
            ID: number;
            ImageUrl: string;
        }[]
    }[]
    DefaultMasterProductImage: {
        Value : {
            ID: number;
            ImageUrl: string;
        }
    }
    MasterProductAttachments: {
        Name: string;
        FileUrl: string;
        ProductAttachmentType: {
            Value: {
                ID: number;
                Description: string;
                DisplayOnWebsite: boolean;
            }
        }
    }[]
    MasterProductImages: {
        ImageUrl: string;
    }[]
    RelatedProductIDs: number[];
    ProductIcons: {
        ID: number;
        IconUrl: string;
        LinkUrl: string;
        Tooltip: string;
        ListOrder: number;
    }[]
}

export const SealProductScreenQuery = gql`
query(
    $id: Int!
) {
    screen: SealProduct(
        id: $id
    ) {
        ID
        Name
        Code
        Description
        Features
        RelatedProductGroupID
        RelatedProducts {
            ID
            Name
            DisplayName
            ProductImages {
                ID
                ImageUrl
            }
        }
        SealProductAttachments {
            Name
            FileUrl
            ProductAttachmentType {
                Value {
                    ID
                    Description
                    DisplayOnWebsite
                }
            }
        }
        SealProductImages {
            ImageUrl
        }
        DefaultSealProductImage {
            Value {
                ID
                ImageUrl
            }
        }
        ProductIcons {
            ID
            IconUrl
            LinkUrl
            Tooltip
            ListOrder
        }
    }
}
`

export const MasterProductScreenQuery = gql`
query(
    $id: Int!
) {
    screen: MasterProduct(
        iD: $id
    ) {
        ID
        Name
        Description
        Features
        Code
        RelatedProductGroupID
        DefaultMasterProductImage {
            Value {
                ID
                ImageUrl
            }
        }
        RelatedProducts {
            ID
            Name
            DisplayName
            ProductImages {
                ID
                ImageUrl
            }
        }
        MasterProductAttachments {
            Name
            FileUrl
            ProductAttachmentType {
                Value {
                    ID
                    Description
                    DisplayOnWebsite
                }
            }
        }
        MasterProductImages {
            ImageUrl
        }
        RelatedProductIDs
        ProductIconIDs
        ProductIcons {
            ID
            IconUrl
            LinkUrl
            Tooltip
            ListOrder
        }
    }
}
`

export type OrderPropType = {
    ID: number;
    ReferenceNumber: string;
    PurchaseOrderNumber: string;
    OrderDate: string;
    NetTotal: number;
    Tax: number;
    GrandTotal: number;
    TrackingNumber: string;
    DeliveryCost: number;
    DeliveryDate: string;
    InvoiceNumber: string;
    InvoicePDF: string;
    InvoiceAddressID: number | null;
    DeliveryAddressID: number | null;
    Paid: boolean;
}

export const OrderScreenQuery = gql`
query(
    $id: Int!
) {
    screen: Order(
        id: $id
    ) {
        ID
        ReferenceNumber
        PurchaseOrderNumber
        OrderDate
        NetTotal
        Tax
        GrandTotal
        TrackingNumber
        DeliveryCost
        DeliveryDate
        InvoiceNumber
        InvoicePDF
        InvoiceAddressID
        DeliveryAddressID
        Paid
    }
}
`
export type QuotePropType = {
    ID: number;
    ReferenceNumber: string;
    QuoteDate: string;
    NetTotal: number;
    Tax: number;
    GrandTotal: number;
    DeliveryCost: number;
    InvoiceAddressID: number | null;
    DeliveryAddressID: number | null;
}

export const QuoteScreenQuery = gql`
query(
    $id: Int!
) {
    screen: Quote(
        id: $id
    ) {
        ID
        ReferenceNumber
        QuoteDate
        NetTotal
        Tax
        GrandTotal
        DeliveryCost
        InvoiceAddressID
        DeliveryAddressID
    }
}
`

export const StripeKey = gql`
query {
    PublishableKey
}`

export const GetCountryQuery = gql`
query(
    $id: Int!
) {
    screen: Country(
        iD: $id
    ) {
        Description
    }
}`

export type ProductRelatedProductsScreenQueryType = {
    screen: {
        items: {
            RelatedProduct: {
                Value?: {
                    ID: number;
                    Name: string;
                    DisplayName: string;
                    ProductImages: {
                        ID: number;
                        ImageUrl: string;
                    }[]
                }
            }
        }[]
    }
}

export const ProductRelatedProductsScreenQuery = gql`
query(
    $id: Int!
) {
    screen: ProductRelatedProducts(
        ProductID: $id
    ) {
        items {
            RelatedProduct {
                Value {
                    ID
                    Name
                    DisplayName
                    ProductImages {
                        ID
                        ImageUrl
                    }
                }
            }
        }
    }
}`

export type RelatedProductGroupScreenQueryType = {
    screen?: {
        RelatedProducts: {
            ID: number;
            Name: string;
            DisplayName: string;
            ProductImages: {
                ID: number;
                ImageUrl: string;
            }[]
        }[]
    }
}

export const RelatedProductGroupScreenQuery = gql`
query(
    $id: Int!
) {
    screen: RelatedProductGroup(
        iD: $id
    ) {
        RelatedProducts {
            ID
            Name
            DisplayName
            ProductImages {
                ID
                ImageUrl
            }
        }
    }
}`

export type ProductPriceScreenQueryType = {
    screen: {
        Price: number;
        POA: boolean;
    }
}


export const ProductPriceScreenQuery = gql`
query(
    $productID: Int!,
    $currencyID: Int!,
    $userID: Int!
) {
    screen: ProductPrice(
        ProductID: $productID,
        CurrencyID: $currencyID,
        UserID: $userID
    ) {
        Price
        POA
    }
}`

export type ShopProductHardwareQueryType = {
    ShopProductHardware: {
        items: {
            Product: {
                ID: number;
                DisplayName: string;
                Name: string;
                ProductSelectorName: string;
                Description: string;
                Features: string;
                Url: string;
                ProductTypeID: number;
                ProductImages: {
                    ID: number;
                    ProductID: number;
                    ImageUrl: string;
                }[]
            }
        }[]
    }
}

export type ShopMasterProductHardwareQueryType = {
    ShopMasterProductHardware: {
        items: {
            ID: number;
            Name: string;
            Description: string;
            Features: string;
            MasterProductImages: {
                ID: number;
                ImageUrl: string;
            }[]
            DefaultMasterProductImage: {
                Value : {
                    ID: number;
                    ImageUrl: string;
                }
            }
        }[]
    }
}

export const ShopProductHardwareQuery = gql`
query(
    $skip: Int,
    $take: Int,
    $order: [ShopProductSortInput!],
    $request: ShopProductHardwareRequestInput
) {
    ShopProductHardware(
        skip: $skip,
        take: $take,
        request: $request,
        order: $order,
    ) {
        items {
            Product {
                ID
                DisplayName
                Name
                ProductSelectorName
                Description
                Features
                Url
                ProductTypeID
                ProductImages {
                    ID
                    ProductID
                    ImageUrl
                }
            }
        }
    }
}`

export const ShopMasterProductHardwareQuery = gql`
query ($skip: Int, $take: Int, $request: ShopMasterProductHardwareRequestInput) {
    ShopMasterProductHardware(
      skip: $skip
      take: $take
      request: $request
    ) {
      items {
          ID
          Name
          Description
          Features
          MasterProductImages {
            ID
            ImageUrl
            __typename
          }
          DefaultMasterProductImage {
            Value {
                ID
                ImageUrl
            }
        }
        __typename
      }
      __typename
    }
  }
  `

export type ShopProductSealsQueryType = {
    ShopProductSeals: {
        items: {
            Product: {
                ID: number;
                DisplayName: string;
                Name: string;
                ProductSelectorName: string;
                Description: string;
                Features: string;
                Url: string;
                ProductImages: {
                    ID: number;
                    ProductID: number;
                    ImageUrl: string;
                }[]
            }
        }[]
    }
}

export const ShopProductSealsQuery = gql`
query(
    $skip: Int,
    $take: Int,
    $order: [ShopProductSortInput!],
    $request: ShopProductSealRequestInput
) {
    ShopProductSeals(
        skip: $skip,
        take: $take,
        request: $request,
        order: $order,
    ) {
        items {
            Product {
                ID
                DisplayName
                Name
                ProductSelectorName
                Description
                Features
                Url
                ProductImages {
                    ID
                    ProductID
                    ImageUrl
                }
            }
        }
    }
}`

export type ShopProductDoorCoresQueryType = {
    ShopProductDoorCores: {
        items: {
            ID: number;
            Name: string;
            MaterialTypeID: number;
            ProductLineImages: {
                ImageUrl: string;
            }[]
        }[]
    }
}

export const ShopProductDoorCoresQuery = gql`
query(
    $skip: Int,
    $take: Int,
    $order: [ProductLineSortInput!],
    $request: ShopProductDoorCoreRequestInput
) {
    ShopProductDoorCores(
        skip: $skip,
        take: $take,
        request: $request,
        order: $order
    ) {
        items {
            ID
            Name
            MaterialTypeID
            ProductLineImages {
                ImageUrl
            }
        }
    }
}`


export type SearchProductsQueryType = {
    SearchProducts: {
        ProductLines: {
            ID: number;
            Name: string;
            MaterialTypeID: number;
            ProductLineImages: {
                ImageUrl: string;
            }[]
        }[],
        Products: {
            ID: number;
            DisplayName: string;
            Name: string;
            ProductSelectorName: string;
            Description: string;
            Features: string;
            Url: string;
            ProductTypeID: number;
            ProductImages: {
                ID: number;
                ProductID: number;
                ImageUrl: string;
            }[]
            Seals : {
                SealProduct : {
                    Value : {
                        SealTypeIDs : number[]
                    }
                }
            }[]
        }[]
    }
}

export const SearchProductsQuery = gql`
query(
    $maxResults: Int,
    $searchText: String,
) {
    SearchProducts(
        maxResults: $maxResults,
        searchText: $searchText
    ) {
        ProductLines {
            ID
            Name
            MaterialTypeID
            ProductLineImages {
                ImageUrl
            }
        }
        Products {
            ID
            DisplayName
            Name
            ProductSelectorName
            Description
            Features
            Url
            ProductTypeID
            ProductImages {
                ID
                ProductID
                ImageUrl
            }
            Seals {
                SealProduct {
                    Value {
                        SealTypeIDs
                    }
                }
            }
        }
    }
}`